import { config, esCaja } from './config'

// Cliente HTTP del sistema.
//
// Concentra tres cosas que si estuvieran repartidas se olvidarían en algún
// lugar: el token de sesión, el header del local, y el refresh automático
// cuando el token vence.

export interface RespuestaApi<T> {
  success: boolean
  data?: T
  message?: string
  errors?: Record<string, string[]>
}

/** Error de la API con la información suficiente para pintarlo en el formulario. */
export class ErrorApi extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly errores?: Record<string, string[]>,
    /** Cuando falta un permiso: cuál, para poder ofrecer la autorización con PIN. */
    readonly permisoFaltante?: string,

    /**
     * El permiso en castellano («Anular ventas»), que es lo que se le muestra
     * a la persona. Lo manda el SERVIDOR: la etiqueta vive en el catálogo del
     * dominio junto con la matriz de roles, y una segunda copia acá sería la
     * que queda vieja.
     */
    readonly permisoEtiqueta?: string,
  ) {
    super(message)
    this.name = 'ErrorApi'
  }

  /** Si la acción se puede desbloquear con el PIN de un encargado. */
  get esFaltaDePermiso() {
    return this.status === 403 && !!this.permisoFaltante
  }
}

/**
 * Si el error es porque no se pudo LLEGAR al servidor, y no porque el servidor
 * haya contestado que no.
 *
 * La diferencia decide cosas serias. Un refresh RECHAZADO quiere decir que la
 * sesión murió y hay que limpiarla; un refresh que nunca llegó a viajar no
 * dice nada sobre el token, y borrarlo por eso deja al cajero afuera por un
 * corte de internet: cuando vuelve tiene que entrar con mail y contraseña, que
 * en la tablet del mostrador —donde se entra con PIN— puede no saber.
 *
 * Todo lo que la API rechaza llega como `ErrorApi`, con su status. Una falla de
 * red llega como el TypeError crudo del fetch, sin status. Ante la duda cuenta
 * como falla de conexión, porque el lado seguro es NO destruir la sesión.
 */
export function esFallaDeConexion(e: unknown): boolean {
  return !(e instanceof ErrorApi)
}

/**
 * El mensaje que se le muestra a la persona, sacado del error.
 *
 * Existe porque el 400 de validación de la nube trae en `message` un texto
 * genérico («Hay errores de validación.») y el motivo de verdad en `errores`,
 * por campo. Un formulario pinta cada campo con el suyo, pero una pantalla que
 * solo tiene un renglón de error —el diálogo de cobro, por ejemplo— mostraba
 * el genérico, que no le dice al cajero qué corregir con el cliente adelante.
 *
 * Toma el primer error de campo si hay, y si no el mensaje. La caja del
 * mostrador ya manda el motivo en `message`, así que las dos APIs terminan
 * mostrando lo mismo, que es lo que se necesita: el SPA es uno solo.
 */
export function mensajeDeError(e: unknown, porDefecto: string): string {
  // El pedido no llegó a viajar. El mensaje por defecto de cada pantalla
  // («No se pudo guardar el proveedor») deja pensando que el sistema rechazó
  // algo del formulario, cuando lo único que falta es conexión.
  if (esFallaDeConexion(e)) {
    return 'No pudimos comunicarnos con el sistema. Revisá la conexión y volvé a intentar.'
  }

  if (!(e instanceof ErrorApi)) return porDefecto

  const primero = Object.values(e.errores ?? {})
    .flat()
    .find((m) => m.trim().length > 0)

  return primero ?? e.message
}

// ── Estado de la sesión ──────────────────────────────────────────────────────
// Vive acá y no en un store de React porque el cliente HTTP lo necesita fuera
// del árbol de componentes (por ejemplo, durante un refresh disparado por un
// request que salió de un efecto que ya se desmontó).

let accessToken: string | null = null
let refreshToken: string | null = null
let tenantSlug: string | null = null

// La caja web elegida en ESTE navegador. Vive acá y no en un store de React
// por lo mismo que el token: el cliente HTTP la necesita afuera del árbol de
// componentes. Se resuelve más abajo, cuando ya existen las claves y el
// lector del almacenamiento.
let cajaWeb: number | null = null

const CLAVE_REFRESH = 'fruver.refresh'
const CLAVE_SLUG = 'fruver.slug'
const CLAVE_CAJA_WEB = 'fruver.caja'

// La sesión de la caja se guarda en sessionStorage, no en localStorage.
//
// Es una decisión distinta a la de la web y tiene motivo. En la caja el token
// lo emite el propio proceso local, vale doce horas y solo sirve contra
// 127.0.0.1: no es una credencial que viaje a ninguna parte. Y la pantalla es
// táctil, así que un F5 accidental pasa todo el tiempo; sin persistir, cada
// resbalón obliga a poner el PIN de nuevo con gente en la fila.
//
// sessionStorage y no localStorage porque cerrar la ventana SÍ tiene que
// cerrar la sesión: es lo que hace el cajero al terminar el turno.
//
// La clave dice «sesion» aunque el almacenamiento ya sea otro: con el mismo
// nombre que la caja web elegida, el día que alguien mueva una de las dos de
// storage el token de la caja pisa el puesto de venta —o al revés—, y el
// síntoma sería una venta cargada a la caja equivocada.
const CLAVE_CAJA = 'fruver.caja.sesion'

export function iniciarSesionLocal(tokens: {
  accessToken: string
  refreshToken: string
  slug: string
}) {
  accessToken = tokens.accessToken
  refreshToken = tokens.refreshToken
  tenantSlug = tokens.slug

  // Solo el refresh token se persiste, nunca el access token: el access vive
  // en memoria y muere al cerrar la pestaña, así que un XSS que lea el
  // almacenamiento no se lleva una credencial de uso inmediato.
  guardar(CLAVE_REFRESH, tokens.refreshToken)
  guardar(CLAVE_SLUG, tokens.slug)
}

// Se lee una vez, al cargar el módulo: es un número en localStorage y no hay
// motivo para volver a mirarlo en cada request.
cajaWeb = numeroGuardado(CLAVE_CAJA_WEB)

/**
 * En qué caja web está parada esta pestaña. Null es «la de fábrica».
 *
 * Se expone para que la pantalla de venta pueda mostrar y cambiar la caja, y
 * para que el resto del sistema no tenga que saber que esto viaja en un
 * header.
 */
export function cajaWebElegida(): number | null {
  return cajaWeb
}

/**
 * Elige la caja web de ESTE navegador.
 *
 * Se guarda en localStorage porque la caja es el PUESTO y no la persona: la
 * tablet del mostrador de la vereda sigue siendo la misma caja mañana, con
 * otro cajero.
 */
export function elegirCajaWeb(id: number | null) {
  cajaWeb = id !== null && id > 0 ? id : null

  if (cajaWeb === null) borrar(CLAVE_CAJA_WEB)
  else guardar(CLAVE_CAJA_WEB, String(cajaWeb))
}

/**
 * Inicia la sesión de la caja: token opaco emitido por el host local, sin
 * refresh (el host lo renueva solo contra la nube; esto es la sesión del
 * cajero contra su propia máquina).
 */
export function iniciarSesionCaja(token: string, slug: string) {
  accessToken = token
  refreshToken = null
  tenantSlug = slug

  guardarSesion(CLAVE_CAJA, token)
  guardar(CLAVE_SLUG, slug)
}

/** Token de caja guardado, para sobrevivir a un F5. */
export function sesionCajaGuardada(): string | null {
  const token = leerSesion(CLAVE_CAJA)
  if (token) accessToken = token

  tenantSlug = leer(CLAVE_SLUG)

  return token
}

export function cerrarSesionLocal() {
  accessToken = null
  refreshToken = null
  borrar(CLAVE_REFRESH)
  borrarSesion(CLAVE_CAJA)
  // El slug se conserva a propósito: la próxima vez que entre, la pantalla de
  // login ya sabe de qué local es y muestra su nombre y su logo.
}

export function sesionGuardada() {
  refreshToken = leer(CLAVE_REFRESH)
  tenantSlug = leer(CLAVE_SLUG)
  return { refreshToken, tenantSlug }
}

// Token de respaldo, para cuando NO hay sesión de local.
//
// Existe por un caso concreto: el panel de plataforma, que tiene su propia
// sesión (ver lib/plataforma) y aun así necesita leer los artículos de ayuda,
// que son los mismos para todo el sistema —texto incrustado en el ensamblado,
// sin base y sin local—. Sin esto el panel sería la única pantalla sin su
// cajón de ayuda.
//
// Va como función registrada y no importando `plataforma.ts` acá: ese módulo
// ya importa este, y el círculo entre los dos es de los que rompen el orden de
// inicialización sin dar ningún error claro.
//
// Ojo con lo que NO hace: no reemplaza a la sesión del local. Si hay una, se
// usa esa; y un pedido a un endpoint de un local hecho con el token de
// plataforma lo rechaza el servidor con un mensaje que lo explica, que es el
// lado correcto para fallar.
let tokenDeRespaldo: (() => string | null) | null = null

export function registrarTokenDeRespaldo(fn: () => string | null) {
  tokenDeRespaldo = fn
}

export const slugActual = () => tenantSlug
export const fijarSlug = (slug: string) => {
  tenantSlug = slug
  guardar(CLAVE_SLUG, slug)
}

// El almacenamiento puede tirar excepción: modo privado, cookies bloqueadas,
// o la captura de miniaturas de algunos navegadores. Nunca debe romper la app.
function numeroGuardado(clave: string): number | null {
  const valor = leer(clave)
  const n = valor === null ? NaN : Number(valor)

  return Number.isInteger(n) && n > 0 ? n : null
}

function guardar(clave: string, valor: string) {
  try {
    localStorage.setItem(clave, valor)
  } catch {
    /* sin persistencia: la sesión dura lo que dure la pestaña */
  }
}
function leer(clave: string): string | null {
  try {
    return localStorage.getItem(clave)
  } catch {
    return null
  }
}
function borrar(clave: string) {
  try {
    localStorage.removeItem(clave)
  } catch {
    /* nada que hacer */
  }
}

// Mismo cuidado para sessionStorage: en modo privado o con las cookies
// bloqueadas, el acceso tira excepción.
function guardarSesion(clave: string, valor: string) {
  try {
    sessionStorage.setItem(clave, valor)
  } catch {
    /* sin persistencia: la sesión dura lo que dure la pestaña */
  }
}
function leerSesion(clave: string): string | null {
  try {
    return sessionStorage.getItem(clave)
  } catch {
    return null
  }
}
function borrarSesion(clave: string) {
  try {
    sessionStorage.removeItem(clave)
  } catch {
    /* nada que hacer */
  }
}

// ── Refresco del token ───────────────────────────────────────────────────────

let refrescando: Promise<boolean> | null = null

/**
 * Renueva el access token. Si ya hay un refresh en curso, se engancha al
 * mismo: sin esto, una pantalla que dispara cinco consultas a la vez con el
 * token vencido haría cinco refreshes, y como el servidor ROTA el token, cuatro
 * de ellos serían un reuso y revocarían toda la cadena de sesión.
 */
async function refrescar(): Promise<boolean> {
  if (refrescando) return refrescando

  refrescando = (async () => {
    if (!refreshToken) return false

    try {
      const res = await fetch(`${config().apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      if (!res.ok) return false

      const json = (await res.json()) as RespuestaApi<{
        accessToken: string
        refreshToken: string
        tenant: { slug: string }
      }>

      if (!json.success || !json.data) return false

      iniciarSesionLocal({
        accessToken: json.data.accessToken,
        refreshToken: json.data.refreshToken,
        slug: json.data.tenant.slug,
      })

      return true
    } catch {
      return false
    } finally {
      refrescando = null
    }
  })()

  return refrescando
}

// Se avisa a la app cuando la sesión murió, para que redirija al login sin que
// cada consulta tenga que saber de routing.
type Escucha = () => void
const alPerderSesion = new Set<Escucha>()

export function onSesionPerdida(fn: Escucha) {
  alPerderSesion.add(fn)
  return () => alPerderSesion.delete(fn)
}

// ── Petición ─────────────────────────────────────────────────────────────────

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  cuerpo?: unknown
  /** Grant de elevación, para la acción que un encargado autorizó con su PIN. */
  grant?: string
  /** No intentar refrescar ante un 401. Lo usa el propio login. */
  sinRefresh?: boolean
  signal?: AbortSignal
  /** A quién se le pregunta. Solo significa algo en el ejecutable (ver `base`). */
  destino?: Destino
}

// ── ¿A quién le pregunta este pedido? ────────────────────────────────────────
//
// En el navegador hay una sola respuesta y esto no existe: todo va a la API del
// local. En el EJECUTABLE hay dos, y la diferencia es el módulo entero de
// Gestión en el mostrador:
//
// - el host LOCAL contesta lo del mostrador: vender, el turno, la balanza, la
//   térmica, el PIN. Anda sin internet, que es el estado normal del local.
// - la NUBE es dueña del catálogo, los precios, los reportes y la
//   configuración. El host local los reenvía por `/nube/...` (ver PuenteNube):
//   la CSP del ejecutable no deja que la pantalla le pegue a otro origen.
//
// Lo decide el ÁREA en la que está parada la persona y no la ruta del pedido,
// y ese es el punto: hay pantallas compartidas que significan cosas distintas
// según desde dónde se abren. Declarar una merma desde el turno es «lo que
// tiré en esta caja» —va al host local, se encola y anda sin internet— y
// declararla desde Gestión es «lo que tiró el local», que es de la nube. El
// mismo componente, dos destinos, sin una línea de código que lo distinga.
//
// Lo que NO depende del área lleva `destino` explícito: el estado de la caja,
// el menú, la ayuda, el PIN y la sesión son de ESTA máquina se mire la
// pantalla que se mire.
type Destino = 'local' | 'nube'

/** Las tres rutas del mostrador. Todo lo demás, en el ejecutable, es Gestión. */
const RUTAS_DEL_MOSTRADOR = ['/', '/turno', '/equipo']

function esAreaDelMostrador(): boolean {
  const ruta = window.location.pathname.replace(/\/+$/, '') || '/'

  return RUTAS_DEL_MOSTRADOR.includes(ruta)
}

/**
 * Si lo que se está haciendo ahora es del MOSTRADOR: el ejecutable, parado en
 * una de sus tres pantallas.
 *
 * Lo exporta para los pocos lugares donde no alcanza con que el pedido salga
 * al destino correcto, porque cambia el PAYLOAD: la merma declarada desde el
 * turno viaja con la cantidad en milésimas y el nombre del motivo congelado
 * (es el contrato de la cola de salida) y la misma pantalla abierta desde
 * Gestión viaja con la forma de la nube. Preguntar `esCaja()` ahí era
 * correcto mientras el ejecutable no tenía Gestión; ahora significaría mandarle
 * a la nube el payload de la caja.
 */
export function enElMostrador(): boolean {
  return esCaja() && esAreaDelMostrador()
}

/**
 * La dirección base de un pedido. En el navegador es siempre la misma; en el
 * ejecutable, la de Gestión pasa por el puente del host local.
 */
function base(destino?: Destino): string {
  const url = config().apiUrl

  if (!esCaja()) return url

  const aDonde = destino ?? (esAreaDelMostrador() ? 'local' : 'nube')

  return aDonde === 'nube' ? `${url}/nube` : url
}

// ── ¿La API contesta? ────────────────────────────────────────────────────────
//
// `navigator.onLine` dice «online» cuando hay wifi pero el módem no llega a
// internet, que es EL caso del local: el cartel de «sin internet» no aparecía y
// el cajero se enteraba al cobrar, con el cliente adelante. Y en un local que
// trabaja todo en línea eso significa que no puede cobrar.
//
// Se resuelve sin sondear nada: la señal son los propios pedidos. Si uno falla
// por red, la API no contesta; si cualquiera vuelve bien, sí contesta. Cero
// tráfico extra y es la pregunta correcta —no «hay internet» sino «la API
// responde»—, que es lo que de verdad importa.
let apiResponde = true

const oyentes = new Set<(responde: boolean) => void>()

function marcarApi(responde: boolean) {
  if (apiResponde === responde) return

  apiResponde = responde
  for (const avisar of oyentes) avisar(responde)
}

/** Si la API contestó la última vez que se le habló. */
export function laApiResponde(): boolean {
  return apiResponde
}

/** Avisa cuando la API deja de contestar (o vuelve). Devuelve cómo darse de baja. */
export function alCambiarLaApi(oyente: (responde: boolean) => void): () => void {
  oyentes.add(oyente)

  // El cleanup de un efecto de React no puede devolver un valor, y `delete`
  // devuelve un booleano: va envuelto en llaves.
  return () => {
    oyentes.delete(oyente)
  }
}

// ── Autorización con el PIN de un encargado ──────────────────────────────────
//
// Cuando una acción choca contra un permiso que el usuario no tiene, el
// servidor contesta 403 diciendo CUÁL, y eso se puede desbloquear con el PIN
// de un encargado (§1.4). Ofrecerlo desde acá y no desde cada pantalla es lo
// que hace que valga para todas: era eso o repetir el mismo try/catch en cada
// mutación que mueve plata, que es la clase de cosa que la pantalla siguiente
// no hace.
//
// Se registra desde afuera (`lib/elevacion`) en lugar de importarlo, porque
// canjear el PIN es un `api.post` y el import sería circular.
type Autorizador = (permiso: string, etiqueta?: string) => Promise<string | null>

let autorizador: Autorizador | null = null

export function fijarAutorizador(fn: Autorizador | null) {
  autorizador = fn
}

async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const ejecutar = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' }

    if (opciones.cuerpo !== undefined) headers['Content-Type'] = 'application/json'

    const token = accessToken ?? tokenDeRespaldo?.() ?? null
    if (token) headers['Authorization'] = `Bearer ${token}`

    // El slug que el SPA está mostrando. El servidor lo valida contra el del
    // token: si no coinciden, 403. Es lo que evita que alguien logueado en un
    // local vea sus propios datos bajo la dirección de otro.
    if (tenantSlug) headers['X-Fruver-Tenant'] = tenantSlug

    if (opciones.grant) headers['X-Fruver-Grant'] = opciones.grant

    // En qué caja WEB está parada esta pestaña.
    //
    // Es lo que permite que un local trabaje todo en línea con más de una
    // persona cobrando: dos navegadores son dos cajas, cada una con su turno y
    // su arqueo. Va por header y no en el token porque el cajero cambia de
    // caja sin volver a entrar, y se guarda por NAVEGADOR (localStorage)
    // porque la caja es el puesto, no la persona.
    //
    // El servidor no le cree: valida que sea una caja web, activa y de este
    // local (ver PosService.PuntoDeVentaAsync).
    if (cajaWeb !== null) headers['X-Fruver-Caja'] = String(cajaWeb)

    try {
      const res = await fetch(`${base(opciones.destino)}${ruta}`, {
        method: opciones.metodo ?? 'GET',
        headers,
        body: opciones.cuerpo !== undefined ? JSON.stringify(opciones.cuerpo) : undefined,
        signal: opciones.signal,
      })

      // Contestó, sea lo que sea: un 400 o un 409 son respuestas del servidor
      // y quieren decir que la API está ahí.
      marcarApi(true)

      return res
    } catch (e) {
      // Un abort NO es falta de conexión: es una consulta que se canceló
      // porque la pantalla cambió, y marcarlo como caída pintaría el cartel de
      // «sin internet» cada vez que alguien navega rápido.
      if (!(e instanceof DOMException && e.name === 'AbortError')) marcarApi(false)

      throw e
    }
  }

  let res = await ejecutar()

  // Un 401 con sesión guardada significa que el access token venció: se
  // renueva y se reintenta UNA vez. Si vuelve a fallar, la sesión murió.
  if (res.status === 401 && !opciones.sinRefresh && refreshToken) {
    if (await refrescar()) {
      res = await ejecutar()
    } else {
      cerrarSesionLocal()
      alPerderSesion.forEach((fn) => fn())
    }
  }

  try {
    return await procesar<T>(res)
  } catch (e) {
    // Falta un permiso y hay quien pueda autorizarlo: se pide el PIN y se
    // reintenta UNA vez con el grant.
    //
    // Solo en las mutaciones. Un GET que da 403 es una pantalla que la persona
    // no tiene que ver, y pedirle el PIN a un encargado para MIRAR un reporte
    // convierte la autorización en un trámite en lugar de en un control. Y
    // solo si no vino ya con grant: si la acción falló CON la autorización
    // puesta, volver a pedirla es un bucle.
    if (
      e instanceof ErrorApi &&
      e.esFaltaDePermiso &&
      e.permisoFaltante &&
      !opciones.grant &&
      opciones.metodo &&
      opciones.metodo !== 'GET' &&
      autorizador
    ) {
      const grant = await autorizador(e.permisoFaltante, e.permisoEtiqueta)

      if (grant) return pedir<T>(ruta, { ...opciones, grant })
    }

    throw e
  }
}

async function procesar<T>(res: Response): Promise<T> {
  // 204 y 304 no traen cuerpo.
  if (res.status === 204 || res.status === 304) return undefined as T

  const texto = await res.text()

  let json: RespuestaApi<T> | null
  try {
    json = texto ? (JSON.parse(texto) as RespuestaApi<T>) : null
  } catch {
    // El servidor devolvió algo que no es JSON: pasa con un 502 del proxy o
    // con una página de error de infraestructura.
    json = null
  }

  if (!res.ok || json?.success === false) {
    // El sobre puede traer el permiso que faltó, que la pantalla usa para
    // decir cuál. No está en el tipo general porque solo lo mandan los 403.
    const cuerpo:
      | (RespuestaApi<T> & { permisoFaltante?: string; permisoEtiqueta?: string })
      | null = json

    throw new ErrorApi(
      cuerpo?.message ?? mensajePorStatus(res.status),
      res.status,
      cuerpo?.errors,
      cuerpo?.permisoFaltante,
      cuerpo?.permisoEtiqueta,
    )
  }

  return (json?.data ?? (undefined as T))
}

// Mensajes en castellano y en términos del usuario, no del protocolo: "Se
// cortó la conexión" sirve, "Bad Gateway" no.
function mensajePorStatus(status: number): string {
  if (status === 401) return 'La sesión venció. Volvé a entrar.'
  if (status === 403) return 'No tenés permiso para esta acción.'
  if (status === 404) return 'No encontramos lo que buscabas.'
  if (status === 409) return 'La operación no se puede hacer en este estado.'
  if (status === 429) return 'Demasiados intentos. Esperá un minuto.'
  if (status === 0 || status >= 500) return 'No pudimos comunicarnos con el servidor.'
  return 'Ocurrió un error inesperado.'
}

// ── API pública ──────────────────────────────────────────────────────────────

export const api = {
  // El segundo argumento acepta las dos formas porque las dos son legítimas y
  // ninguna es mayoría: casi todas las consultas solo pasan su `signal`, y las
  // pocas que hablan con ESTA máquina en el ejecutable necesitan decir el
  // destino. Obligar a `{ signal }` en las doscientas llamadas que ya existen
  // sería un cambio enorme para servir a diez.
  get: <T>(ruta: string, extra?: AbortSignal | Partial<Opciones>) =>
    pedir<T>(ruta, extra instanceof AbortSignal ? { signal: extra } : { ...extra }),

  post: <T>(ruta: string, cuerpo?: unknown, extra?: Partial<Opciones>) =>
    pedir<T>(ruta, { metodo: 'POST', cuerpo, ...extra }),

  put: <T>(ruta: string, cuerpo?: unknown, extra?: Partial<Opciones>) =>
    pedir<T>(ruta, { metodo: 'PUT', cuerpo, ...extra }),

  delete: <T>(ruta: string, extra?: Partial<Opciones>) =>
    pedir<T>(ruta, { metodo: 'DELETE', ...extra }),

  /** Login: no reintenta con refresh, porque justamente todavía no hay sesión. */
  login: <T>(ruta: string, cuerpo: unknown, destino?: Destino) =>
    pedir<T>(ruta, { metodo: 'POST', cuerpo, sinRefresh: true, destino }),

  /**
   * Subida de archivos. No usa `pedir` porque el cuerpo es multipart y no hay
   * que poner Content-Type a mano: el navegador tiene que agregar el boundary.
   */
  subirImagen: async <T>(ruta: string, archivo: File, destino?: Destino): Promise<T> => {
    const form = new FormData()
    form.append('archivo', archivo)

    const headers: Record<string, string> = { Accept: 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    if (tenantSlug) headers['X-Fruver-Tenant'] = tenantSlug

    const res = await fetch(`${base(destino)}${ruta}`, {
      method: 'POST',
      headers,
      body: form,
    })

    return procesar<T>(res)
  },

  /**
   * Baja un archivo binario y dispara la descarga del navegador.
   *
   * No usa `pedir` porque la respuesta no es el sobre JSON de la API: es el
   * archivo. Y va por `fetch` y no por un `<a href>` por el gotcha de siempre
   * —la ruta pide token y un enlace no manda el header de autorización—, lo
   * que obliga a pasar por un `blob:`, o sea a tenerlo entero en memoria de la
   * pestaña. Para la exportación de un local eso alcanza de sobra; el que
   * puede no entrar es el `.bak` de la base, que por eso tiene su descarga por
   * token aparte.
   *
   * El nombre sale del `Content-Disposition`, que lo manda el servidor: es el
   * que sabe cómo se llama lo que generó.
   */
  bajarArchivo: async (ruta: string, sugerido: string, destino?: Destino): Promise<void> => {
    const headers: Record<string, string> = {}
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    if (tenantSlug) headers['X-Fruver-Tenant'] = tenantSlug

    const res = await fetch(`${base(destino)}${ruta}`, { headers })

    if (!res.ok) {
      // El error sí viene en el sobre de siempre, así que se lee como JSON.
      let mensaje = `No se pudo generar el archivo (${res.status}).`
      try {
        const sobre = (await res.json()) as { message?: string }
        if (sobre.message) mensaje = sobre.message
      } catch {
        /* el cuerpo no era JSON */
      }
      throw new ErrorApi(mensaje, res.status)
    }

    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(
      res.headers.get('Content-Disposition') ?? '',
    )
    const url = URL.createObjectURL(await res.blob())
    const a = document.createElement('a')
    a.href = url
    a.download = m?.[1] ? decodeURIComponent(m[1]) : sugerido
    a.click()
    URL.revokeObjectURL(url)
  },

  /** URL absoluta de una imagen, para usar en un src. */
  urlImagen: (ruta: string | null | undefined, destino?: Destino) => {
    if (!ruta) return undefined
    if (ruta.startsWith('http')) return ruta

    // Las rutas del servidor vienen como /api/uploads/... y apiUrl ya incluye
    // el /api, así que se saca para no duplicarlo. Y pasa por la MISMA regla
    // de destino que el resto: en el ejecutable, la miniatura de un producto
    // que se está editando en Gestión la sirve la nube, y la de la grilla del
    // POS la sirve la caja con los bytes que bajó la sincronización.
    return `${base(destino)}${ruta.replace(/^\/api/, '')}`
  },
}
